import { v4 as uuidv4 } from 'uuid';
import { format, addMinutes } from 'date-fns';
import CryptoJS from 'crypto-js';
import { toast } from 'sonner';
import { PasswordStrength, STORAGE_KEYS } from './types';

// Constants
const ENCRYPTION_KEY = 'NALC_SECURE_ENCRYPTION_KEY';
const MAX_LOGIN_ATTEMPTS = 5;
const LOCKOUT_MINUTES = 15;
const MIN_PASSWORD_LENGTH = 8;
const SECURE_PASSKEY = 'EDGTU70HFDJ73GDJAW124GFSVSH73H'; // Standardized secure passkey

// Helper functions
export const encryptData = (data: string, salt: string = ''): string => {
  const combinedKey = `${ENCRYPTION_KEY}${salt}`;
  return CryptoJS.AES.encrypt(data, combinedKey).toString();
};

export const decryptData = (encryptedData: string, salt: string = ''): string | null => {
  try {
    const combinedKey = `${ENCRYPTION_KEY}${salt}`;
    const bytes = CryptoJS.AES.decrypt(encryptedData, combinedKey);
    return bytes.toString(CryptoJS.enc.Utf8);
  } catch (error) {
    console.error('Decryption failed:', error);
    return null;
  }
};

// Passkey generation
export const generatePasskey = (): string => {
  return uuidv4().split('-')[0].toUpperCase();
};

// Date formatting
export const formatLockoutTime = (date: Date): string => {
  return format(date, 'yyyy-MM-dd HH:mm:ss');
};

export const calculateLockoutEndTime = (): Date => {
  return addMinutes(new Date(), LOCKOUT_MINUTES);
};

export const isLockoutExpired = (lockoutEndTime: string | null): boolean => {
  if (!lockoutEndTime) return true;
  
  try {
    const lockoutDate = new Date(lockoutEndTime);
    const currentDate = new Date();
    return currentDate > lockoutDate;
  } catch (error) {
    console.error('Error parsing lockout date:', error);
    return true;
  }
};

export const calculateTimeRemaining = (lockoutEndTime: string | null): string => {
  if (!lockoutEndTime) return '0 minutes';
  
  try {
    const lockoutDate = new Date(lockoutEndTime);
    const currentDate = new Date();
    
    if (currentDate > lockoutDate) {
      return '0 minutes';
    }
    
    const diffMs = lockoutDate.getTime() - currentDate.getTime();
    const diffMins = Math.ceil(diffMs / 60000);
    
    if (diffMins < 1) {
      return 'Less than a minute';
    }
    
    return `${diffMins} ${diffMins === 1 ? 'minute' : 'minutes'}`;
  } catch (error) {
    console.error('Error calculating time remaining:', error);
    return '0 minutes';
  }
};

// Notification functions
export const notifySuccess = (message: string) => {
  toast.success(message);
};

export const notifyError = (message: string) => {
  toast.error(message);
};

// Password strength validation
export const validatePassword = (password: string): PasswordStrength => {
  if (!password) {
    return { isValid: false, message: 'Password is required' };
  }
  
  if (password.length < MIN_PASSWORD_LENGTH) {
    return { 
      isValid: false, 
      message: `Password must be at least ${MIN_PASSWORD_LENGTH} characters long` 
    };
  }
  
  if (!/[A-Z]/.test(password)) {
    return {
      isValid: false,
      message: 'Password must contain at least one uppercase letter'
    };
  }
  
  if (!/[a-z]/.test(password)) {
    return {
      isValid: false,
      message: 'Password must contain at least one lowercase letter'
    };
  }
  
  if (!/[0-9]/.test(password)) {
    return {
      isValid: false,
      message: 'Password must contain at least one number'
    };
  }
  
  return { isValid: true, message: 'Password is strong' };
};

// Add missing utility functions that are required by PasswordContext.tsx
export const hashPassword = (password: string): string => {
  // Simple hash for demo purposes
  return btoa(password + 'salt');
};

export const savePasswordToStorage = (hash: string): void => {
  localStorage.setItem(STORAGE_KEYS.PASSWORD_HASH, hash);
};

export const createAuthSession = (): void => {
  const expiry = new Date();
  expiry.setHours(expiry.getHours() + 24);
  
  localStorage.setItem(STORAGE_KEYS.AUTH_SESSION, JSON.stringify({
    expires: expiry.toISOString(),
    created: new Date().toISOString()
  }));
};

export const isAuthSessionValid = (): boolean => {
  const session = localStorage.getItem(STORAGE_KEYS.AUTH_SESSION);
  if (!session) return false;
  
  try {
    const { expires } = JSON.parse(session);
    return new Date(expires) > new Date();
  } catch (e) {
    return false;
  }
};

export const clearAuthSession = (): void => {
  localStorage.removeItem(STORAGE_KEYS.AUTH_SESSION);
};

export const saveLockoutInfo = (attempts: number, endTime: Date | null): void => {
  localStorage.setItem(STORAGE_KEYS.LOCKOUT_INFO, JSON.stringify({
    attempts,
    endTime: endTime ? endTime.toISOString() : null
  }));
};

export const generateSecurePassKey = (): string => {
  // Use the standardized secure pass key for emergency access
  return SECURE_PASSKEY;
};

export const initializeSecurePassKey = (): void => {
  // Always ensure the passkey is set to the standardized value
  localStorage.setItem(STORAGE_KEYS.SECURE_PASSKEY, SECURE_PASSKEY);
  console.log('Secure passkey initialized with standardized value');
};

export const getSecurePassKey = (): string | null => {
  const passkey = localStorage.getItem(STORAGE_KEYS.SECURE_PASSKEY);
  // Return the standardized secure passkey
  return passkey || SECURE_PASSKEY;
};

export const verifySecurePassKey = (passkey: string): boolean => {
  console.log('Verifying secure passkey against standardized value');
  
  // Direct comparison with the standardized passkey
  const isValid = passkey === SECURE_PASSKEY;
  
  if (isValid) {
    // Reset attempts and create auth session
    createAuthSession();
    playNotificationSound();
    toast.success("Emergency access granted with VIP Pass Key!");
    return true;
  }
  
  toast.error("Invalid VIP Pass Key");
  return false;
};

export const playNotificationSound = (): void => {
  try {
    const audio = new Audio('/assets/sounds/notification.mp3');
    audio.play().catch(e => console.error('Error playing sound:', e));
  } catch (e) {
    console.error('Error initializing sound:', e);
  }
};
